import { motion } from 'framer-motion'
import { useAnimationConfig } from '../hooks/useReducedMotion'
import { hasAnyBackdropFilter } from '../utils/supportsGlassmorphism'

interface GlassContainerProps {
  children: React.ReactNode
  className?: string
  style?: React.CSSProperties
}

/**
 * Glass Container Component - FIXED VERSION
 * Wraps the entire radar in a glassmorphic card
 * Uses CSS class 'glass-container' instead of CSS vars in inline styles
 * 
 * Phase 1.1: Fixed CSS Custom Properties in Inline Styles
 */
export function GlassContainer({ children, className = '', style = {} }: GlassContainerProps) {
  const animConfig = useAnimationConfig()

  // Check for backdrop-filter support
  const supportsGlass = hasAnyBackdropFilter()

  return (
    <motion.div
      className={`relative overflow-hidden glass-container ${className}`}
      style={{
        borderRadius: '24px',
        // Fallback for non-glass browsers
        ...(!supportsGlass && {
          background: 'rgba(255, 255, 255, 0.95)',
          boxShadow: '0 4px 24px rgba(0, 0, 0, 0.1)',
        }),
        ...style,
      }}
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={animConfig.reducedMotion
        ? { duration: 0 }
        : { type: 'spring' as const, stiffness: 400, damping: 30 }
      }
    >
      {/* Inner gradient overlay for depth */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: supportsGlass
            ? `linear-gradient(
                180deg,
                rgba(255, 255, 255, 0.1) 0%,
                transparent 50%,
                rgba(0, 0, 0, 0.02) 100%
              )`
            : `linear-gradient(
                180deg,
                rgba(255, 255, 255, 0.5) 0%,
                rgba(255, 255, 255, 0) 50%,
                rgba(0, 0, 0, 0.02) 100%
              )`,
          borderRadius: 'inherit',
        }}
      />

      {/* Fallback notice for developers (only in dev mode) */}
      {import.meta.env?.MODE === 'development' && !supportsGlass && (
        <div
          className="absolute top-0 right-0 px-2 py-1 text-[10px] font-mono"
          style={{
            background: 'rgba(255, 193, 7, 0.9)',
            color: '#000',
            borderRadius: '0 0 0 8px',
            zIndex: 100,
          }}
        >
          ⚠ Fallback Mode
        </div>
      )}

      {/* Content */}
      <div className="relative z-10">
        {children}
      </div>
    </motion.div>
  )
}
